import React, { useEffect, useRef, useState } from 'react';

type Props = {
  /** ms to wait after mount before assuming the Clerk form is stuck */
  timeoutMs?: number;
  /** CSS selector that should exist once Clerk has finished rendering its form */
  detectSelector?: string;
};

const TroubleSigningIn: React.FC<Props> = ({
  timeoutMs = 15000,
  detectSelector = '.cl-formButtonPrimary, .cl-socialButtonsBlockButton, .cl-formFieldInput',
}) => {
  const [stuck, setStuck] = useState(false);
  const [open, setOpen] = useState(false);
  const detectedRef = useRef(false);

  useEffect(() => {
    const start = Date.now();

    const check = () => {
      if (detectedRef.current) return;
      if (document.querySelector(detectSelector)) {
        detectedRef.current = true;
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        setStuck(true);
      }
    };

    const interval = window.setInterval(check, 1000);
    const finalTimer = window.setTimeout(() => {
      check();
      window.clearInterval(interval);
    }, timeoutMs + 500);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(finalTimer);
    };
  }, [timeoutMs, detectSelector]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      {stuck && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-pink-lavender/40 bg-pink-lavender/10 p-4 text-left"
        >
          <p className="text-space-cadet font-medium text-[0.95rem] mb-1">
            Stuck on a loading screen?
          </p>
          <p className="text-cool-gray text-sm leading-relaxed">
            If the form is spinning or won't move past “verifying”, it's usually a
            sign-in step on our end that we can clear quickly — see{' '}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-space-cadet underline font-medium hover:text-space-cadet/80"
            >
              Trouble signing in?
            </button>
          </p>
        </div>
      )}

      <div className="text-center mt-5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-cool-gray text-sm underline-offset-2 hover:underline hover:text-space-cadet transition-colors min-h-[44px] px-2"
        >
          Trouble signing in?
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="trouble-signing-in-title"
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-elegant-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-[0_8px_32px_rgba(25,40,92,0.25)] max-w-[560px] w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center text-cool-gray hover:text-space-cadet text-2xl leading-none"
            >
              ×
            </button>

            <div className="p-6 sm:p-8">
              <h2
                id="trouble-signing-in-title"
                className="font-serif text-[1.5rem] sm:text-[1.75rem] font-semibold text-space-cadet mb-2"
              >
                Trouble signing in?
              </h2>
              <p className="text-cool-gray text-sm sm:text-base mb-5">
                If you're stuck on a loading or “verifying” screen after entering your
                details, that's almost always a sign-in verification step on our end —
                not a problem with your device. The quickest fix is to email us and
                we'll unblock your account, usually within a day.
              </p>

              <ol className="space-y-4 text-elegant-black text-sm sm:text-base">
                <li>
                  <span className="font-medium text-space-cadet">
                    1. Stuck on a “verifying” or loading spinner? Email us.
                  </span>{' '}
                  This is the most common cause and we can clear it from our side
                  quickly — there's no need to keep retrying. Reach us at{' '}
                  <a
                    href="mailto:support@cyrano.cloud?subject=Cyrano%20sign-in%20trouble"
                    className="text-space-cadet underline font-medium"
                  >
                    support@cyrano.cloud
                  </a>.
                </li>
                <li>
                  <span className="font-medium text-space-cadet">
                    2. Form never appears at all?
                  </span>{' '}
                  That usually points to a network or security block. Try a
                  private/incognito window, or open this page on mobile data with
                  Wi-Fi off — if it loads fine there, your home/office network or
                  security software is filtering us.
                </li>
                <li>
                  <span className="font-medium text-space-cadet">
                    3. Whitelist our login system (for that network case).
                  </span>{' '}
                  In your antivirus, browser security extension, or router DNS filter,
                  allow these domains:
                  <div className="mt-2 rounded-md bg-warm-background/60 border border-cool-gray/20 p-3 font-mono text-xs sm:text-sm break-all">
                    clerk.cyrano.cloud<br />
                    *.clerk.accounts.dev<br />
                    cyrano.cloud
                  </div>
                  Common culprits: Norton, McAfee, Bitdefender, Pi-hole, NextDNS,
                  OpenDNS, school or workplace firewalls.
                </li>
                <li>
                  <span className="font-medium text-space-cadet">
                    4. Tell us what you saw.
                  </span>{' '}
                  When you email{' '}
                  <a
                    href="mailto:support@cyrano.cloud?subject=Cyrano%20sign-in%20trouble"
                    className="text-space-cadet underline font-medium"
                  >
                    support@cyrano.cloud
                  </a>
                  , let us know whether you saw a stuck loading screen or no form at
                  all — it helps us fix it faster.
                </li>
              </ol>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-6 w-full py-3 bg-space-cadet hover:bg-space-cadet/90 text-white rounded-lg font-medium text-base transition-all min-h-[44px]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TroubleSigningIn;
